import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
   RecordingGroupPermission,
   RecordingPermission,
} from '../../call-recording-extensions-list/models/recording-permissions.model';
import { UserReportViewModel } from '../../report-permissions-list/models/user-report-permission.model';
import { UserPrivilegeFormValue } from '../models/user-detail-form';
import { UserDetailView } from '../models/user-detail-view.model';
import { GetUserDetail_user_userReportsConnection_items } from '../queries/generated/GetUserDetail';
import { State, userDetailFeatureKey } from './user-detail.reducer';

export const userDetailFeature = createFeatureSelector<State>(userDetailFeatureKey);

export const getUser = createSelector(userDetailFeature, (state) => {
   return state.user;
});

export const getUserCallState = createSelector(userDetailFeature, (state) => {
   return state.callState;
});

export const getUserDetailExtensions = createSelector(userDetailFeature, (state) => {
   return state.extensions;
});

export const getUserDetailExtensionGroups = createSelector(userDetailFeature, (state) => {
   return state.extensionGroups;
});

export const getUserDetailSelector = createSelector(
   getUser,
   getUserDetailExtensions,
   getUserDetailExtensionGroups,
   (userData, extensions, extensionGroups): UserDetailView | null => {
      if (!userData) {
         return null;
      }

      const { userCallRecordingExtensionsConnection, userCallRecordingExtensionGroupsConnection, ...user } = userData;

      const extensionRecordingPermissionsMap: { [key: number]: RecordingPermission } = {};

      const extensionGroupRecordingPermissionsMap: { [key: number]: RecordingGroupPermission } = {};

      for (const recordingPermission of userCallRecordingExtensionsConnection!.items) {
         extensionRecordingPermissionsMap[recordingPermission.extension.extensionId] = {
            ...recordingPermission,
            id: recordingPermission.userCallRecordingExtensionId,
            isDeleted: false,
         };
      }

      for (const recordingPermission of userCallRecordingExtensionGroupsConnection!.items) {
         extensionGroupRecordingPermissionsMap[recordingPermission.extensionGroupId] = {
            ...recordingPermission,
            id: recordingPermission.userCallRecordingExtensionGroupId,
            isDeleted: false,
         };
      }

      const extensionPermissions: RecordingPermission[] = [];

      const extensionGroupPermissions: RecordingGroupPermission[] = [];

      for (const extension of extensions) {
         const permission = extensionRecordingPermissionsMap[extension.extensionId];
         let newExtensionPermission: RecordingPermission;

         if (permission) {
            newExtensionPermission = {
               extension,
               id: permission.id,
               isInclude: permission.isInclude,
               isDeleted: permission.isDeleted,
            };
         } else {
            newExtensionPermission = {
               extension,
               id: 0,
               isInclude: false,
               isDeleted: false,
            };
         }
         extensionPermissions.push(newExtensionPermission);
      }

      for (const extensionGroup of extensionGroups) {
         const permission = extensionGroupRecordingPermissionsMap[extensionGroup.extensionGroupId];
         let newExtensionPermission: RecordingGroupPermission;

         if (permission) {
            newExtensionPermission = {
               extensionGroup,
               id: permission.id,
               isInclude: permission.isInclude,
               isDeleted: permission.isDeleted,
            };
         } else {
            newExtensionPermission = {
               extensionGroup,
               id: 0,
               isInclude: false,
               isDeleted: false,
            };
         }
         extensionGroupPermissions.push(newExtensionPermission);
      }

      return {
         ...user,
         userRecordingExtensions: {
            allExtensionsIsSelected: user.hasAllExtensionRecordings,
            allExtensionsIsSelectedDirty: false,
            extensionRecordingPermissions: extensionPermissions,
            extensionGroupRecordingPermissions: extensionGroupPermissions,
         },
         account: user.account,
         userReports: user.userReportsConnection?.items
            ? mapUserReportsConnectionItems(user.userReportsConnection.items)
            : null,
         extensionGroup: null, //Cannot tie an extension group to an account
         privileges: user.privilegesConnection.items.reduce((acc, userPrivilege) => {
            acc[userPrivilege.privilegeId] = {
               userPrivilegeId: userPrivilege.userPrivilegeId,
               userId: userPrivilege.userId,
               accountId: userPrivilege.accountId,
               privilegeId: userPrivilege.privilegeId,
               isAdd: userPrivilege.isAdd,
               isDelete: userPrivilege.isDelete,
               isEdit: userPrivilege.isEdit,
               isEnabled: userPrivilege.isEnabled,
               isView: userPrivilege.isView,
            };
            return acc;
         }, {} as { [privilegeId: string]: UserPrivilegeFormValue }),
      };
   }
);
function mapUserReportsConnectionItems(items: GetUserDetail_user_userReportsConnection_items[]): UserReportViewModel[] {
   return items
      .filter((i) => i.report.reportDefinition != null)
      .map((i): UserReportViewModel => {
         return {
            reportId: i.report.reportDefinition!,
            userId: i.userId,
            userReportId: i.userReportId,
         };
      });
}
